import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowLeft } from 'lucide-react';
import { Section } from '../components/ui/Section';
import { CTASection } from '../components/sections/CTASection';
import { useServices } from '../hooks/useServices';
import { useProjects } from '../hooks/useProjects';
const cardColors = ['bg-blue-50', 'bg-amber-50', 'bg-green-50', 'bg-purple-50', 'bg-rose-50', 'bg-cyan-50'];
// Detect if a URL points to a video file
function isVideo(url: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase().split('?')[0];
  return ['.mp4', '.webm', '.mov', '.ogg'].some(ext => lower.endsWith(ext));
}
// Render image or video based on URL
function MediaElement({
  src,
  alt,
  className,
  autoPlay = true
}: {
  src: string;
  alt: string;
  className?: string;
  autoPlay?: boolean;
}) {
  if (isVideo(src)) {
    return <video src={src} className={className} autoPlay={autoPlay} loop muted playsInline data-id="element-752" />;
  }
  return <img src={src} alt={alt} className={className} data-id="element-753" />;
}
export function ServiceDetailPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    services,
    loading: servicesLoading
  } = useServices();
  const {
    projects,
    loading: projectsLoading
  } = useProjects();
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (servicesLoading) {
    return <main className="pt-32 pb-20 min-h-screen flex items-center justify-center" data-id="element-754">
        <div className="animate-pulse flex flex-col items-center" data-id="element-755">
          <div className="w-12 h-12 border-4 border-[#00316B] border-t-transparent rounded-full animate-spin mb-4" data-id="element-756" />
          <p className="text-gray-500" data-id="element-757">Loading service details...</p>
        </div>
      </main>;
  }
  const service = services.find(s => s.slug === slug);
  if (!service) {
    return <main className="pt-32 pb-20 min-h-screen" data-id="element-758">
        <Section background="default" data-id="element-759">
          <div className="text-center max-w-2xl mx-auto" data-id="element-760">
            <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-761">
              Service Not Found
            </h1>
            <p className="text-gray-600 mb-8" data-id="element-762">
              The service you're looking for doesn't exist or has been removed.
            </p>
            <Link to="/services" className="inline-flex items-center text-[#FF6600] font-medium hover:text-[#CC5200] transition-colors" data-id="element-763">
              <ArrowLeft className="w-4 h-4 mr-2" data-id="element-764" />
              Back to Services
            </Link>
          </div>
        </Section>
      </main>;
  }
  // Find projects that match this service's title
  const relatedProjects = projects.filter(p => p.category === service.title);
  return <main data-id="element-765">
      {/* Full-bleed Hero Banner */}
      <div className="relative w-full h-[280px] md:h-[340px] lg:h-[380px] bg-[#00316B]" data-id="element-766">
        {service.image && <MediaElement src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover opacity-60" data-id="element-767" />}
        {/* Blue overlay */}
        <div className="absolute inset-0 bg-[#00316B]/50" data-id="element-768" />

        {/* Content on top of image */}
        <div className="relative z-10 h-full flex flex-col justify-end max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14" data-id="element-769">
          <Link to="/services" className="inline-flex items-center text-sm font-medium text-white/70 hover:text-white transition-colors mb-6 w-fit" data-id="element-770">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-771" />
            All Services
          </Link>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-white leading-tight" data-id="element-772">
            {service.title}
          </motion.h1>
        </div>
      </div>

      {/* Description Section */}
      <Section background="default" data-id="element-773">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="prose prose-lg text-gray-600 leading-relaxed whitespace-pre-wrap w-full max-w-none" data-id="element-774">
          {service.description}
        </motion.div>
      </Section>

      {/* What We Offer — Specialities from Contentful */}
      {service.specialities && service.specialities.length > 0 && <Section background="white" data-id="element-775">
          <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-3xl md:text-4xl font-bold font-heading text-[#0C0C0C] mb-12" data-id="element-776">
            What we offer
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-777">
            {service.specialities.map((spec, index) => {
          const placeholderImages = ['https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1522542550221-31fd19575a2d?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=500&fit=crop', 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&h=500&fit=crop'];
          const imgSrc = spec.image || placeholderImages[index % placeholderImages.length];
          return <motion.div key={spec.id} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100" data-id="element-778">
                  <div className="aspect-[8/5] bg-gray-100 overflow-hidden" data-id="element-779">
                    <MediaElement src={imgSrc} alt={spec.title} className="w-full h-full object-cover" data-id="element-780" />
                  </div>
                  <div className="border-l-4 border-[#FF6600] p-6" data-id="element-781">
                    <h3 className="text-lg font-bold font-heading text-[#0C0C0C] mb-2" data-id="element-782">
                      {spec.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm" data-id="element-783">
                      {spec.description}
                    </p>
                  </div>
                </motion.div>;
        })}
          </div>
        </Section>}

      {/* Related Projects Section */}
      <Section background="white" data-id="element-784">
        <div className="mb-12" data-id="element-785">
          <h2 className="text-3xl font-bold font-heading text-[#0C0C0C]" data-id="element-786">
            Selected Work
          </h2>
          <p className="text-gray-600 mt-2" data-id="element-787">
            Recent projects in {service.title.toLowerCase()}
          </p>
        </div>

        {projectsLoading ? <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse" data-id="element-788">
            {[1, 2].map(i => <div key={i} data-id="element-789">
                <div className="aspect-[4/3] rounded-xl bg-gray-200 mb-6" data-id="element-790" />
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" data-id="element-791" />
                <div className="h-4 bg-gray-100 rounded w-1/2" data-id="element-792" />
              </div>)}
          </div> : relatedProjects.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-793">
            {relatedProjects.map((project, index) => {
          const bgColor = cardColors[index % cardColors.length];
          const href = project.live_url || '#';
          return <motion.article key={project.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="group cursor-pointer" data-id="element-794">
                  <a href={href} target={project.live_url ? '_blank' : '_self'} rel="noopener noreferrer" className="block" data-id="element-795">
                    <div className={`aspect-[4/3] rounded-xl overflow-hidden mb-6 ${project.cover_image ? 'bg-gray-100' : bgColor} relative`} data-id="element-796">
                      {project.cover_image ? <MediaElement src={project.cover_image} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-797" /> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-798">
                          <div className="w-3/4 h-3/4 bg-white/50 rounded-lg shadow-lg flex items-center justify-center" data-id="element-799">
                            <span className="text-gray-400 text-sm" data-id="element-800">
                              Project Preview
                            </span>
                          </div>
                        </div>}
                      <div className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md" data-id="element-801">
                        <ArrowUpRight className="w-5 h-5 text-[#0C0C0C]" data-id="element-802" />
                      </div>
                    </div>

                    <div className="mb-3" data-id="element-803">
                      <h3 className="text-2xl font-bold font-heading text-[#0C0C0C] group-hover:text-[#00316B] transition-colors" data-id="element-804">
                        {project.title}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-805">
                        {project.client_name ? `Client: ${project.client_name}` : project.category}
                      </p>
                    </div>

                    <p className="text-gray-600 mb-4 leading-relaxed line-clamp-2" data-id="element-806">
                      {project.description}
                    </p>
                  </a>
                </motion.article>;
        })}
          </div> : <div className="bg-gray-50 rounded-2xl p-12 text-center" data-id="element-807">
            <p className="text-gray-500" data-id="element-808">
              No projects showcased in this category yet. Check back soon!
            </p>
          </div>}
      </Section>

      <CTASection data-id="element-809" />
    </main>;
}